<template>
  <div id="guestproductcontentdownloader">
    <!-- ── Expired state ─────────────────────────────────────────────────── -->
    <v-card
      v-if="oTPInfo.processed && !oTPInfo.isValid"
      class="mx-auto"
      max-width="100%"
      outlined
    >
      <v-row no-gutters class="align-center">
        <v-card-title class="text-h4">
          {{
            $i18n.t("headings.order") +
            " " +
            $i18n.t("headings.download") +
            " " +
            $i18n.t("headings.expired")
          }}
        </v-card-title>
      </v-row>
    </v-card>

    <!-- ── Valid order state ─────────────────────────────────────────────── -->
    <v-card
      v-else-if="oTPInfo.processed && oTPInfo.isValid"
      class="mx-auto"
      max-width="100%"
      outlined
    >
      <v-card-title class="text-h4">
        {{
          order.orderID +
          ": " +
          $i18n.t("headings.product") +
          " " +
          $i18n.t("headings.download")
        }}
      </v-card-title>

      <v-card-subtitle class="text-h6 primary--text">
        {{
          order.downloadDurationLeft +
          " " +
          $i18n.t("headings.downloadDurationLeft")
        }}
      </v-card-subtitle>

      <v-card-title class="pt-0">
        <v-row no-gutters>
          <v-col cols="12">
            <v-checkbox
              color="primary"
              v-model="termsAccepted"
              label="Before downloading, please tick the checkbox to confirm that you have clicked and read the Terms and Conditions below and that you agree these resources are for your personal use only. You may not share these resources under any circumstances via WhatsApp, email, Telegram, Facebook, or any other platform."
            ></v-checkbox>
            <v-btn
              class="mx-2"
              small
              color="primary"
              dark
              @click="dialogTermsAndConditions = true"
            >
              <span style="font-size: 1.1em"> TERMS &amp; CONDITIONS </span>
            </v-btn>
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
              clearable
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-title>

      <div class="px-4">
        <v-data-table
          :mobile-breakpoint="0"
          :footer-props="{ showFirstLastPage: true }"
          :headers="headers"
          :items="order.orderProducts"
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          must-sort
          :search="search"
          show-expand
          single-expand
          item-key="id"
          no-data-text="No products found"
          :loading="!order.orderProducts || order.orderProducts.length === 0"
          loading-text="Loading products..."
          @item-expanded="onItemExpanded"
        >
          <!-- ── Title column: shows bundle badge when applicable ─────────── -->
          <template v-slot:[`item.title`]="{ item }">
            <div>
              <span>{{ item.title }}</span>
              <v-chip
                v-if="item.bundleTitle"
                x-small
                label
                class="ml-2 font-weight-medium"
                color="blue-grey lighten-4"
                text-color="blue-grey darken-2"
              >
                <v-icon x-small left>mdi-package-variant</v-icon>
                {{ item.bundleTitle }}
              </v-chip>
            </div>
          </template>

          <!-- ── Expand / Fetch button ───────────────────────────────────── -->
          <template
            v-slot:[`item.data-table-expand`]="{ item, isExpanded, expand }"
          >
            <v-btn
              small
              depressed
              color="primary"
              :loading="item.loading"
              :disabled="!termsAccepted"
              @click="expand(!isExpanded)"
            >
              <v-icon left>
                {{
                  isExpanded
                    ? "mdi-collapse-all-outline"
                    : "mdi-cloud-download-outline"
                }}
              </v-icon>
              {{ isExpanded ? "Hide" : "Fetch" }}
            </v-btn>
          </template>

          <!-- ── Expanded row: content for the selected product ─────────── -->
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-4 grey lighten-5">
              <v-card flat outlined :loading="item.loading">
                <v-data-table
                  :mobile-breakpoint="0"
                  :headers="subHeaders"
                  hide-default-header
                  :items="flattenContent(item.contentData)"
                  :items-per-page="-1"
                  hide-default-footer
                  class="elevation-0"
                  no-data-text="No content found for this product."
                >
                  <!-- Addon type chip -->
                  <template v-slot:[`item.addonType`]="{ item: content }">
                    <v-chip
                      v-if="content.addonType"
                      x-small
                      label
                      outlined
                      color="primary"
                      class="font-weight-bold"
                    >
                      {{ content.addonType }}
                    </v-chip>
                  </template>

                  <!-- Action buttons -->
                  <template v-slot:[`item.action`]="{ item: content }">
                    <div class="d-flex justify-end">
                      <!-- Watch video -->
                      <v-tooltip v-if="content.url" top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            v-bind="attrs"
                            v-on="on"
                            :disabled="!termsAccepted"
                            @click.stop="handleVideoAction(content)"
                          >
                            <v-icon color="primary">mdi-play-circle</v-icon>
                          </v-btn>
                        </template>
                        <span>Watch Video</span>
                      </v-tooltip>

                      <!--
                        Download file.
                        The presigned S3 URL (`content.fileUrl`) was generated
                        server-side in the lambda — we simply open it directly.
                        No blob fetching or client-side URL construction needed.
                      -->
                      <v-tooltip v-if="content.fileUrl" top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            v-bind="attrs"
                            v-on="on"
                            :disabled="!termsAccepted"
                            @click.stop="handleFileAction(content)"
                          >
                            <v-icon color="primary">
                              {{
                                getIcon(content.fileType || content.fileName)
                              }}
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>Download File</span>
                      </v-tooltip>
                    </div>
                  </template>
                </v-data-table>
              </v-card>
            </td>
          </template>
        </v-data-table>
      </div>
    </v-card>

    <!-- ── Video preview dialog ──────────────────────────────────────────── -->
    <v-dialog v-model="previewDialog" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="headline grey lighten-2">
          {{ selectedContent.title || "Preview" }}
          <v-spacer></v-spacer>
          <v-btn icon @click="previewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <div v-if="selectedContent.url" class="video-container">
            <VideoEmbed
              :src="selectedContent.url"
              :autoplay="false"
              :controls="true"
              :loop="false"
            />
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <TermsAndConditionsDialog
      v-if="dialogTermsAndConditions"
      @closeDialogTermsAndConditions="closeDialogTermsAndConditions"
    />
  </div>
</template>

<script>
import { API } from "aws-amplify";
import VideoEmbed from "@/components/global-components/VideoEmbed.vue";
import TermsAndConditionsDialog from "@/core/services/TermsAndConditionsDialog.vue";

export default {
  name: "GuestProductContentDownloader",

  components: { VideoEmbed, TermsAndConditionsDialog },

  props: {
    orderId: { type: String, required: true },
    oTP: { type: String, required: true },
  },

  data: () => ({
    search: "",
    sortBy: "title",
    sortDesc: true,
    previewDialog: false,
    selectedContent: {},
    termsAccepted: false,
    dialogTermsAndConditions: false,

    headers: [
      { text: "Title", value: "title", sortable: true, groupable: false },
      { text: "", value: "data-table-expand", sortable: false },
    ],
    subHeaders: [
      { text: "Name", value: "title", sortable: false, groupable: false },
      {
        text: "Addon Type",
        value: "addonType",
        sortable: false,
        groupable: false,
      },
      { text: "Action", value: "action", sortable: false, align: "right" },
    ],

    order: { orderID: "", orderProducts: [] },
    oTPInfo: { isValid: false, processed: false },
  }),

  created() {
    this.fetchOrder();
  },

  methods: {
    // ── Order loading ──────────────────────────────────────────────────────

    async fetchOrder() {
      try {
        const response = await API.get(
          "productcontentdownloaderapi",
          `/order/${this.orderId}/${this.oTP}`,
        );
        this.order = response.order;
        this.oTPInfo = {
          isValid: !!(this.order && this.order.orderID),
          processed: true,
        };
      } catch (error) {
        console.error("Error fetching order:", error);
        this.oTPInfo = { isValid: false, processed: true };
      }
    },

    // ── Per-product content fetching ───────────────────────────────────────
    //
    // Called when the user clicks "Fetch" on a specific product row.
    // This triggers the lambda to generate presigned S3 URLs for that
    // product only, and return them to the client.

    onItemExpanded({ item, value }) {
      // Only fetch if expanding and we haven't loaded this product's content yet
      if (value && !item.contentData) {
        this.fetchProductContent(item);
      }
    },

    async fetchProductContent(product) {
      this.$set(product, "loading", true);
      try {
        const response = await API.get(
          "productcontentdownloaderapi",
          `/productcontent/${product.id}`,
          {
            queryStringParameters: {
              // Send the addons the customer purchased so the lambda
              // only returns presigned URLs for those specific addons.
              addons: JSON.stringify(product.addons || []),
            },
          },
        );
        // contentData.files / .urls / .addons all contain presigned URLs
        // that were generated server-side in the lambda — never on the client.
        this.$set(product, "contentData", response.contentData);
      } catch (error) {
        console.error("Error fetching product content:", error);
      } finally {
        this.$set(product, "loading", false);
      }
    },

    // ── Content display helpers ────────────────────────────────────────────

    flattenContent(contentData) {
      if (!contentData) return [];
      const files = (contentData.files || []).map((f) => ({ ...f }));
      const addons = (contentData.addons || []).map((a) => ({ ...a }));
      const urls = (contentData.urls || []).map((u) => ({
        title: "Video",
        fileType: "URL",
        url: u,
      }));
      return [...files, ...addons, ...urls];
    },

    getIcon(typeOrName) {
      const name = typeOrName?.toLowerCase() || "";
      if (name.includes("pdf")) return "mdi-file-pdf-box";
      if (name.includes("png") || name.includes("jpg") || name.includes("webp"))
        return "mdi-image";
      return "mdi-file-download-outline";
    },

    // ── Action handlers ────────────────────────────────────────────────────

    handleVideoAction(content) {
      this.selectedContent = content;
      this.previewDialog = true;
    },

    handleFileAction(content) {
      if (!this.termsAccepted) return;

      // The presigned URL was generated server-side in the lambda.
      // Open it directly — no need to fetch the blob through the browser.
      // Using window.open triggers the browser's native download behaviour
      // and works for all file sizes without memory pressure.
      const link = document.createElement("a");
      link.href = content.fileUrl;
      link.setAttribute("download", content.fileName || "download");
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
      document.body.appendChild(link);
      link.click();
      link.remove();
    },

    openDialogTermsAndConditions() {
      this.dialogTermsAndConditions = true;
    },
    closeDialogTermsAndConditions() {
      this.dialogTermsAndConditions = false;
    },
  },
};
</script>

<style scoped>
.video-container {
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  background: #000;
}
</style>
